import {TouchableOpacity, ScrollView, Platform} from 'react-native'
import React, {useEffect, useState, useRef} from 'react'
import {NativeStackScreenProps} from '@react-navigation/native-stack';

import {PrivateStackParamList} from '@app/navigationApp/types';
import {useAppDispatch, useAppSelector} from "@store/hooks";
import ScreenWrapper from "@hoc/ScreenWrapper";
import CommonHeader from "@sharedComponents/Header/Header";
import Stepper from "@sharedComponents/Stepper/Stepper";
import {Theme, ThemedText, ThemedView} from "@app/theme";
import {IndividualInfoCard} from "@screens/KYC/KYCSummary/components/IndividualInfoCard";
import UserInfo from "../../../../assets/Icons/UserInfo";
import DobIcon from "../../../../assets/Icons/DobIcon";
import AddressIcon from "../../../../assets/Icons/AddressIcon";
import CheckBox from "@sharedComponents/CheckBox/CheckBox";
import {useTheme} from "@shopify/restyle";
import {
    kycSummaryEnhanceLimit,
    kycSummaryInitiate,
    kycSummaryUpdate,
    kycSummaryVerify
} from "@screens/KYC/KYCSummary/services/services";
import {formatEpochToDateString, trackEvent} from "@app/utils";
import KYCUpdateView from './components/KYCUpdateView';
import KYCSummaryBottomView from './components/KYCSummaryBottomView';
import KYCUpdateConfirmationModal from './components/KYCUpdateConfirmationModal';
import KYCSummarySuccess from './components/KYCSummarySuccess';
import KYCSummaryErrorComponent from "@screens/KYC/KYCSummary/components/KYCSummaryErrorComponent";
import {useStepperData} from "@hooks/useStepperData";
import {useSuccessLottie} from "@hooks/useSuccessLottie";
import PageLoader from "@sharedComponents/PageLoader/PageLoader";
import {getNextRoute} from '../../../navigationApp/navigationUtil';
import { updateLinkedApplication } from '@app/store/slices/authSlice';

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'KYC_SUMMARY'>;


const KYCSummary = ({navigation, route}: ScreenProps) => {

    const {userDetails} = useAppSelector(state => state.auth)
    const {colors} = useTheme<Theme>()
    const [checkBox1Checked, setCheckBox1Checked] = useState(true)
    const [checkBox2Checked, setCheckBox2Checked] = useState(true)
    const [pageLoading, setPageLoading] = useState(false)
    const [kycSummaryDetails, setKYCSummaryDetails] = useState<any>()
    const [showKYCUpdate, setShowKYCUpdate] = useState(false)
    const [showKYCUpdateConfirmationModal, setShowKYCUpdateConfirmationModal] = useState(false)
    const [nextStepId, setNextStepId] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [ctaLoader, setCTALoader] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const {stepperData , isLoading: stepperIsLoading} = useStepperData()
    const { lottie: successLottie } = useSuccessLottie();
    const [stepStatusMap, setStepStatusMap] = useState('')
    const [applicationState, setApplicationState] = useState('')
    const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const dispatch = useAppDispatch()


    const fetchKYCSummaryDetails = async () => {
        try {
            const response = await kycSummaryInitiate({applicationId: userDetails?.linkedApplications[0]?.applicationId});
            setKYCSummaryDetails(response?.data?.stepResponseObject);
        } catch (error) {
            setShowErrorModal(true)
            //@ts-ignore
            setErrorMessage(error.data.message)
        }
    };

    const fetchKYCSummaryEnhanceLimitDetails = async () => {
        try {
            const response = await kycSummaryEnhanceLimit({applicationId: userDetails?.linkedApplications[0]?.applicationId});
            setShowKYCUpdate(response?.data?.isEnabled);
        } catch (error) {
            setShowErrorModal(true)
            //@ts-ignore
            setErrorMessage(error.data.message)
        }
    };

    const handleKYCUpdate = async () => {
        try {
            const response = await kycSummaryUpdate({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
            dispatch(updateLinkedApplication(response?.data))
            setShowKYCUpdateConfirmationModal(false)
            const calculatedNextRoute = getNextRoute(response?.data?.currentStepId, response?.data.applicationState, response?.data.stepStatusMap)
            if (Platform.OS === 'web') {
                navigation.navigate(calculatedNextRoute as any)
            } else {
                navigation.reset({
                    index: 0,
                    routes: [{ name: 'HOME' }, { name: 'KYC_STEPPER' }, { name: calculatedNextRoute }],
                })

            }
        } catch (error) {
            setShowErrorModal(true)
            //@ts-ignore
            setErrorMessage(error?.data?.message || "Verification failed")
        }
    }

    useEffect(() => {
        setPageLoading(true);
    Promise.all([
            fetchKYCSummaryDetails(),
            fetchKYCSummaryEnhanceLimitDetails(),
        ]).finally(() => {
            setPageLoading(false);
        })
    }, []);

    const handleSuccess = async () => {
        setCTALoader(true)
        try {
            const response = await kycSummaryVerify({applicationId: userDetails?.linkedApplications[0]?.applicationId});
            setShowKYCUpdate(response?.data?.isEnabled);
            const currentStepId = response?.data?.updatedApplicationObj?.currentStepId
            const applicationState = response?.data?.updatedApplicationObj?.applicationState
            const stepStatusMap = response?.data?.updatedApplicationObj?.stepStatusMap
            setNextStepId(currentStepId)
            setApplicationState(applicationState)
            setStepStatusMap(stepStatusMap)
            setShowSuccessModal(true)
            const timeout = setTimeout(async() => {
                navigation.reset({
                    index: 0,
                    routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(currentStepId,applicationState,stepStatusMap) }]
                })
                setShowSuccessModal(false)
            }, 3000);
            navigationTimeoutRef.current = timeout;
        } catch (error) {
            setShowSuccessModal(false)
        }
    }

    const handleNextNavigation = () => {
        if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
            navigationTimeoutRef.current = null;
        }
        setShowSuccessModal(false);
        const nextRoute = getNextRoute(nextStepId, applicationState, stepStatusMap)
        if(Platform.OS === 'web') {
            navigation.navigate(nextRoute);
        } else {
            navigation.reset({
                index: 0,
                routes: [{name : "HOME"} ,{ name: 'KYC_STEPPER' }, { name: nextRoute }]
            })
        }
    }


    const handleCheckBox1Toggle = (): void => {
        setCheckBox1Checked(!checkBox1Checked)
    };
    const handleCheckBox2Toggle = (): void => {
        setCheckBox2Checked(!checkBox2Checked)
    };

    const [kycSummaryData, setKycSummaryData] = useState([
        {
            title: "PAN",
            value: "-",
            icon: UserInfo
        },
        {
            title: "Date of birth",
            value: "-",
            icon: DobIcon
        },
        {
            title: "Address",
            value: "-",
            icon: AddressIcon
        }
    ]);

    useEffect(() => {
        if (kycSummaryDetails && userDetails) {
            setKycSummaryData([
                {
                    title: "Full name",
                    value: userDetails?.linkedBorrowerAccounts[0]?.accountHolderName,
                    icon: <UserInfo bg={colors['bg-fill-emphasis-primary-1']} color={colors['icon-action-primary']} />
                },
                {
                    title: "Date of birth",
                    value: kycSummaryDetails?.dob ? formatEpochToDateString(Number(kycSummaryDetails?.dob)) : "-",
                    icon: <DobIcon bg={colors['bg-fill-emphasis-primary-1']} color={colors['icon-action-primary']} />
                },
                {
                    title: "Address",
                    value: kycSummaryDetails?.address,
                    icon: <AddressIcon bg={colors['bg-fill-emphasis-primary-1']} color={colors['icon-action-primary']} />
                }
            ]);
        }
    }, [kycSummaryDetails?.address, userDetails, kycSummaryDetails?.dob, kycSummaryDetails?.fullName]);


    return (
        <ScreenWrapper screenName={route?.name} additionalProperties={{newArchRoute: true}}>
            {pageLoading || stepperIsLoading ? (
                <PageLoader />
            ) : (
                <ThemedView backgroundColor="bg-surface-primary" flex={1}>
                    <CommonHeader
                        label='KYC verification'
                        navigation={navigation}
                        pageName={route.name}
                        hideBottomBorder={true}
                    />
                    <Stepper stepperData={stepperData}/>
                    <ScrollView style={{ paddingBottom: 20}}>
                    {ctaLoader && <ThemedView zIndex={100} opacity={0.5} position='absolute' top={0} left={0} right={0} bottom={0} flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'/>}
                        <ThemedView paddingHorizontal={"lg"} paddingTop={"lg"}>
                            <ThemedText variant={"h5s"} color={"text-primary"} mb={"2xl"}>
                                Verify your KYC details
                            </ThemedText>
                            {kycSummaryData.map((item, index) => (
                                <IndividualInfoCard title={item.title} value={item.value} icon={item.icon}
                                                    showBorderPadding={index !== kycSummaryData.length - 1}/>
                            ))}
                          <TouchableOpacity onPress={() => {
                            handleCheckBox1Toggle()
                            trackEvent('KYC_SUMMARY_CONFIRM_RESIDENT_OF_INDIA_CLICKED')
                          }}>
                            <ThemedView flexDirection={"row"} mt={"md"}>
                              <CheckBox size={16} onToggle={handleCheckBox1Toggle} isChecked={checkBox1Checked}/>
                              <ThemedText variant={"b4m"} color={"text-secondary"} ml={"sm"}>
                                I confirm that I am a resident of India.
                              </ThemedText>
                            </ThemedView>
                          </TouchableOpacity>
                           <TouchableOpacity
                            onPress={() => {
                            handleCheckBox2Toggle()
                            trackEvent('KYC_SUMMARY_CONFIRM_CURRENT_ADDRESS_CLICKED')
                           }}>
                             <ThemedView flexDirection={"row"} mt={"md"}>
                               <CheckBox size={16} onToggle={handleCheckBox2Toggle} isChecked={checkBox2Checked}/>
                               <ThemedText variant={"b4m"} color={"text-secondary"} mr='lg' ml={"sm"}>
                                 I confirm that my current address is same as permanent address as shown above.
                               </ThemedText>
                             </ThemedView>
                           </TouchableOpacity>

                        </ThemedView>
                    </ScrollView>
                    {showKYCUpdate ? (
                        <KYCUpdateView
                            checkBox1Checked={checkBox1Checked}
                            checkBox2Checked={checkBox2Checked}
                            handleSuccess={handleSuccess}
                            setShowKYCUpdateConfirmationModal={setShowKYCUpdateConfirmationModal}
                            colors={colors}
                        />
                    ) : (
                        <KYCSummaryBottomView
                            checkBox1Checked={checkBox1Checked}
                            checkBox2Checked={checkBox2Checked}
                            handleSuccess={handleSuccess}
                            colors={colors}
                            ctaLoader={ctaLoader}
                        />
                    )}
                    {showKYCUpdateConfirmationModal && (
                        <KYCUpdateConfirmationModal
                            isVisible={showKYCUpdateConfirmationModal}
                            onClose={() => setShowKYCUpdateConfirmationModal(false)}
                            handleKYCUpdate={handleKYCUpdate}
                            colors={colors}
                        />
                    )}
                    {showSuccessModal && (
                        <KYCSummarySuccess
                            isVisible={showSuccessModal}
                            onClose={() => setShowSuccessModal(false)}
                            successLottie={successLottie}
                            handleNextNavigation={handleNextNavigation}
                            colors={colors}
                        />
                    )}
                    {showErrorModal &&
                        <KYCSummaryErrorComponent
                            isVisible={showErrorModal}
                            onClose={() => {
                                setShowErrorModal(false);
                            }}
                            errorMessage={errorMessage}
                            colors={colors}
                            setShowErrorModal={setShowErrorModal} />
                    }
                </ThemedView>
            )}
        </ScreenWrapper>

    )
}

export default KYCSummary

